import type {GuestProductSnapshot} from './types';
import {SNAPSHOT_STALE_MS} from './types';
import type {
	Gsoa_ProductCatalog_Eshop_ProductCategoryEshop as ProductCategory,
	Gsoa_ProductCatalog_ProductDetail as ProductDetail,
	Gsoa_ShoppingList_GetItemsCollection as ShoppingListItem,
} from '~/openapi';
import {GsoaProductCatalogService} from '~/openapi';
import {GsoaParamBuilder} from '~/utils/GsoaParamBuilder';
import {useProductCatalogStore} from '~/stores/productCatalog';

const BATCH_CHUNK_SIZE = 50;

const pendingVanrsByHouse = new Map<number, Set<string>>();
const flushPromiseByHouse = new Map<number, Promise<void>>();

export function buildSnapshotFromProduct(
	product: ProductDetail,
	houseNumber?: number,
	mainCategory?: ProductCategory | null,
): GuestProductSnapshot {
	return {
		imgIcon: product.imgIcon ?? product.imgThumbnail ?? product.imgDetail,
		imgDetail: product.imgDetail ?? product.imgThumbnail ?? product.imgIcon,
		unitId: product.unitId,
		unitAmount: product.unitAmount,
		productCategories: product.productCategories,
		ribbon: product.ribbon,
		badges: product.badges,
		calculatedPrice: product.calculatedPrice,
		productInHouse: product.productInHouse,
		mainCategory: mainCategory ?? null,
		fetchedAt: new Date().toISOString(),
		houseNumber,
	};
}

export function getProductSnapshotCacheKey(houseNumber: number, vanr: string): string {
	return `${houseNumber}:${vanr}`;
}

export function needsProductEnrichment(
	item: Pick<ShoppingListItem, 'VANR' | 'productDetail'>,
	houseNumber: number,
): boolean {
	const detail = item.productDetail as GuestProductSnapshot | undefined;
	if (detail?.unavailable) {
		return false;
	}
	if (!detail || !item.VANR) {
		return true;
	}
	if (detail.houseNumber !== houseNumber) {
		return true;
	}
	if (!detail.fetchedAt) {
		return true;
	}
	const fetchedAt = new Date(detail.fetchedAt).getTime();
	return Number.isNaN(fetchedAt) || Date.now() - fetchedAt > SNAPSHOT_STALE_MS;
}

function unavailableSnapshot(houseNumber: number): GuestProductSnapshot {
	return {unavailable: true, fetchedAt: new Date().toISOString(), houseNumber};
}

function getCachedSnapshot(
	vanr: string,
	houseNumber: number,
	cache: Map<string, GuestProductSnapshot>,
): GuestProductSnapshot | undefined {
	return cache.get(getProductSnapshotCacheKey(houseNumber, vanr));
}

function isCachedSnapshotUsable(
	vanr: string,
	houseNumber: number,
	cache: Map<string, GuestProductSnapshot>,
): boolean {
	const cached = getCachedSnapshot(vanr, houseNumber, cache);
	return !!cached && !needsProductEnrichment({VANR: vanr, productDetail: cached}, houseNumber);
}

export async function fetchProductsByVanrs(
	vanrs: string[],
	houseNumber: number,
	cache: Map<string, GuestProductSnapshot>,
): Promise<void> {
	const uniqueVanrs = [...new Set(vanrs)].filter((vanr) => !isCachedSnapshotUsable(vanr, houseNumber, cache));
	if (!uniqueVanrs.length) {
		return;
	}

	const productCatalogStore = useProductCatalogStore();
	await productCatalogStore.fetchCategories();

	for (let offset = 0; offset < uniqueVanrs.length; offset += BATCH_CHUNK_SIZE) {
		const chunk = uniqueVanrs.slice(offset, offset + BATCH_CHUNK_SIZE);
		try {
			const response = await GsoaProductCatalogService.getHouseProducts({
				houseNumber,
				filter: GsoaParamBuilder.inArray('vanr', chunk),
				pageSize: chunk.length,
				listedProductOnly: true,
			});

			const foundVanrs = new Set<string>();
			for (const product of response?.products ?? []) {
				if (!product.vanr) {
					continue;
				}
				foundVanrs.add(product.vanr);
				cache.set(
					getProductSnapshotCacheKey(houseNumber, product.vanr),
					buildSnapshotFromProduct(
						product,
						houseNumber,
						productCatalogStore.resolveMainCategory(product.productCategories),
					),
				);
			}

			for (const vanr of chunk) {
				if (!foundVanrs.has(vanr)) {
					cache.set(getProductSnapshotCacheKey(houseNumber, vanr), unavailableSnapshot(houseNumber));
				}
			}
		} catch {
			for (const vanr of chunk) {
				cache.set(getProductSnapshotCacheKey(houseNumber, vanr), unavailableSnapshot(houseNumber));
			}
		}
	}
}

async function flushPendingVanrs(
	houseNumber: number,
	cache: Map<string, GuestProductSnapshot>,
): Promise<void> {
	const pending = pendingVanrsByHouse.get(houseNumber);
	pendingVanrsByHouse.delete(houseNumber);
	if (!pending?.size) {
		return;
	}
	await fetchProductsByVanrs([...pending], houseNumber, cache);
}

function scheduleBatchFetch(
	vanrs: string[],
	houseNumber: number,
	cache: Map<string, GuestProductSnapshot>,
): Promise<void> {
	const missingVanrs = vanrs.filter((vanr) => !isCachedSnapshotUsable(vanr, houseNumber, cache));
	if (!missingVanrs.length) {
		return Promise.resolve();
	}

	let pending = pendingVanrsByHouse.get(houseNumber);
	if (!pending) {
		pending = new Set();
		pendingVanrsByHouse.set(houseNumber, pending);
	}
	for (const vanr of missingVanrs) {
		pending.add(vanr);
	}

	let flushPromise = flushPromiseByHouse.get(houseNumber);
	if (!flushPromise) {
		flushPromise = new Promise<void>((resolve, reject) => {
			queueMicrotask(async () => {
				try {
					await flushPendingVanrs(houseNumber, cache);
					resolve();
				} catch (error) {
					reject(error);
				}
			});
		}).finally(() => {
			flushPromiseByHouse.delete(houseNumber);
		});
		flushPromiseByHouse.set(houseNumber, flushPromise);
	}

	return flushPromise;
}

function applySnapshotsToItems(
	items: ShoppingListItem[],
	houseNumber: number,
	cache: Map<string, GuestProductSnapshot>,
): boolean {
	let changed = false;
	for (const item of items) {
		if (!item.VANR) {
			continue;
		}
		const snapshot = getCachedSnapshot(item.VANR, houseNumber, cache);
		if (snapshot && item.productDetail !== snapshot) {
			item.productDetail = snapshot;
			changed = true;
		}
	}
	return changed;
}

export async function enrichGuestItems(
	items: ShoppingListItem[],
	houseNumber: number,
	options?: {
		cache?: Map<string, GuestProductSnapshot>;
	},
): Promise<boolean> {
	const cache = options?.cache ?? new Map<string, GuestProductSnapshot>();
	const itemsToEnrich = items.filter((item) => needsProductEnrichment(item, houseNumber));
	if (!itemsToEnrich.length) {
		return false;
	}

	let changed = applySnapshotsToItems(itemsToEnrich, houseNumber, cache);

	const vanrsToFetch = [...new Set(
		itemsToEnrich
			.filter((item) => item.VANR && needsProductEnrichment(item, houseNumber))
			.map((item) => item.VANR!),
	)];
	if (!vanrsToFetch.length) {
		return changed;
	}

	await scheduleBatchFetch(vanrsToFetch, houseNumber, cache);

	if (applySnapshotsToItems(itemsToEnrich, houseNumber, cache)) {
		changed = true;
	}

	const stillMissingVanrs = vanrsToFetch.filter((vanr) => !isCachedSnapshotUsable(vanr, houseNumber, cache));
	if (stillMissingVanrs.length) {
		await scheduleBatchFetch(stillMissingVanrs, houseNumber, cache);
		if (applySnapshotsToItems(itemsToEnrich, houseNumber, cache)) {
			changed = true;
		}
	}

	return changed;
}
